import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../utils/api';

const PREDEFINED_TAGS = ['IDEA', 'WORRY', 'BLOCKER', 'QUESTION', 'SUGGESTION', 'DECISION_NEEDED'];

export default function IdeasTab() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [filterTag, setFilterTag] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [editingTags, setEditingTags] = useState(false);
  const [tempTags, setTempTags] = useState([]);

  const loadIdeas = useCallback(async () => {
    try {
      setLoading(true);
      let url = '/api/ideas';
      if (filterStatus) {
        url += `?status=${filterStatus}`;
      }
      const data = await apiCall(url);
      setIdeas(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load ideas');
      console.error('Ideas error:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    loadIdeas();
  }, [loadIdeas]);

  const formatDateTime = (date, time) => {
    if (!date) return 'Unknown';
    const [year, month, day] = date.split('-');
    const [hours, mins] = (time || '00:00:00').split(':');
    return new Date(`${year}-${month}-${day}T${hours}:${mins}:00`).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleTagToggle = (tag) => {
    setTempTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleSaveTags = async () => {
    if (!selectedIdea) return;

    try {
      await apiCall(`/api/ideas/${selectedIdea.id}/update`, {
        method: 'POST',
        body: JSON.stringify({ tags: tempTags }),
      });

      setIdeas((prev) =>
        prev.map((idea) =>
          idea.id === selectedIdea.id ? { ...idea, tags: tempTags } : idea
        )
      );

      setSelectedIdea({ ...selectedIdea, tags: tempTags });
      setEditingTags(false);
    } catch (err) {
      console.error('Failed to update tags:', err);
      alert('Failed to save tags. Please try again.');
    }
  };

  const handleStatusChange = async (status) => {
    if (!selectedIdea) return;

    try {
      await apiCall(`/api/ideas/${selectedIdea.id}/update`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      });

      setIdeas((prev) =>
        prev.map((idea) =>
          idea.id === selectedIdea.id ? { ...idea, status } : idea
        )
      );

      setSelectedIdea({ ...selectedIdea, status });
    } catch (err) {
      console.error('Failed to update status:', err);
      alert('Failed to update status. Please try again.');
    }
  };

  const filteredIdeas = filterTag
    ? ideas.filter((idea) => (idea.tags || []).includes(filterTag))
    : ideas;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-lg font-bold mb-2">Loading ideas...</div>
          <div className="text-on-surface-variant">Please wait</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card bg-error-container p-6 text-center">
        <p className="font-bold text-error mb-2">Error Loading Ideas</p>
        <p className="text-sm text-on-error-container mb-4">{error}</p>
        <button onClick={loadIdeas} className="btn-primary">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="card p-4 flex flex-col sm:flex-row gap-4">
        <div>
          <label className="text-xs font-semibold text-on-surface-variant uppercase mb-1 block">
            Filter by Tag
          </label>
          <select
            value={filterTag}
            onChange={(e) => setFilterTag(e.target.value)}
            className="border border-outline rounded px-3 py-2 text-sm bg-surface text-on-surface"
          >
            <option value="">All Tags</option>
            {PREDEFINED_TAGS.map((tag) => (
              <option key={tag} value={tag}>
                {tag}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-semibold text-on-surface-variant uppercase mb-1 block">
            Filter by Status
          </label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-outline rounded px-3 py-2 text-sm bg-surface text-on-surface"
          >
            <option value="">All Status</option>
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="dismissed">Dismissed</option>
          </select>
        </div>

        <div className="flex-1" />

        <div>
          <label className="text-xs font-semibold text-on-surface-variant uppercase mb-1 block">
            Total Ideas
          </label>
          <div className="text-2xl font-bold text-primary">{filteredIdeas.length}</div>
        </div>
      </div>

      {filteredIdeas.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-2xl mb-2">💡</p>
          <p className="text-lg font-bold mb-2">No Ideas</p>
          <p className="text-on-surface-variant">
            {filterTag || filterStatus
              ? 'No ideas match the selected filters.'
              : 'Ideas will appear here when received from ideas@eastgrinsteadac.co.uk'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Ideas List */}
          <div className="lg:col-span-2 space-y-2">
            {filteredIdeas.map((idea) => (
              <div
                key={idea.id}
                onClick={() => {
                  setSelectedIdea(idea);
                  setTempTags(idea.tags || []);
                  setEditingTags(false);
                }}
                className={`card p-4 cursor-pointer transition-colors ${
                  selectedIdea?.id === idea.id
                    ? 'bg-primary-fixed border-l-4 border-primary'
                    : 'hover:bg-surface-container-high border-l-4 border-outline'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-on-surface truncate">{idea.subject}</p>
                    <p className="text-xs text-on-surface-variant mt-1">From: {idea.sender}</p>
                    <p className="text-xs text-on-surface-variant mt-0.5">
                      {formatDateTime(idea.received_date, idea.received_time)}
                    </p>
                    {idea.tags && idea.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {idea.tags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-block text-xs bg-secondary-fixed text-on-secondary-fixed px-2 py-1 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="mt-2">
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded ${
                          idea.status === 'new'
                            ? 'bg-error-fixed text-on-error-fixed'
                            : idea.status === 'reviewed'
                              ? 'bg-tertiary-fixed text-on-tertiary-fixed'
                              : 'bg-surface-dim text-on-surface'
                        }`}
                      >
                        {idea.status}
                      </span>
                    </div>
                  </div>
                  {selectedIdea?.id === idea.id && <span className="ml-2 text-primary">→</span>}
                </div>
              </div>
            ))}
          </div>

          {/* Detail Pane */}
          <div className="lg:col-span-1">
            {selectedIdea ? (
              <div className="card p-4 sticky top-36 max-h-96 overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-on-surface">Details</h3>
                  <button
                    onClick={() => setSelectedIdea(null)}
                    className="text-on-surface-variant hover:text-on-surface text-lg"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-bold text-on-surface-variant uppercase mb-1">
                      Subject
                    </p>
                    <p className="text-sm text-on-surface break-words">{selectedIdea.subject}</p>
                  </div>

                  <div>
                    <p className="text-xs font-bold text-on-surface-variant uppercase mb-1">
                      From
                    </p>
                    <p className="text-sm text-on-surface break-words">{selectedIdea.sender}</p>
                  </div>

                  <div>
                    <p className="text-xs font-bold text-on-surface-variant uppercase mb-1">
                      Received
                    </p>
                    <p className="text-sm text-on-surface">
                      {formatDateTime(selectedIdea.received_date, selectedIdea.received_time)}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-bold text-on-surface-variant uppercase mb-2">
                      Status
                    </p>
                    <select
                      value={selectedIdea.status || 'new'}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      className="w-full border border-outline rounded px-2 py-1 text-xs bg-surface text-on-surface"
                    >
                      <option value="new">New</option>
                      <option value="reviewed">Reviewed</option>
                      <option value="dismissed">Dismissed</option>
                    </select>
                  </div>

                  <div className="pt-2 border-t border-outline-variant">
                    <p className="text-xs font-bold text-on-surface-variant uppercase mb-2">
                      Tags
                    </p>
                    {!editingTags ? (
                      <>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {tempTags.length > 0 ? (
                            tempTags.map((tag) => (
                              <span
                                key={tag}
                                className="inline-block text-xs bg-primary-fixed text-on-primary-fixed px-2 py-1 rounded"
                              >
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-on-surface-variant italic">No tags</span>
                          )}
                        </div>
                        <button
                          onClick={() => setEditingTags(true)}
                          className="w-full text-xs bg-surface-variant text-on-surface-variant hover:bg-primary hover:text-on-primary px-2 py-1 rounded font-semibold"
                        >
                          Edit Tags
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="space-y-1 mb-2 max-h-40 overflow-y-auto">
                          {PREDEFINED_TAGS.map((tag) => (
                            <label key={tag} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={tempTags.includes(tag)}
                                onChange={() => handleTagToggle(tag)}
                                className="w-4 h-4"
                              />
                              <span className="text-xs text-on-surface">{tag}</span>
                            </label>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveTags}
                            className="flex-1 text-xs bg-primary text-on-primary px-2 py-1 rounded font-semibold hover:bg-primary-dark"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingTags(false);
                              setTempTags(selectedIdea.tags || []);
                            }}
                            className="flex-1 text-xs bg-surface-variant text-on-surface-variant px-2 py-1 rounded font-semibold hover:bg-outline"
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  {selectedIdea.body && (
                    <div className="pt-2 border-t border-outline-variant">
                      <p className="text-xs font-bold text-on-surface-variant uppercase mb-2">
                        Message
                      </p>
                      <p className="text-xs text-on-surface leading-relaxed whitespace-pre-wrap">
                        {selectedIdea.body}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="card p-4 text-center text-on-surface-variant sticky top-36">
                <p className="text-sm">Select an idea to view details</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
